/**
 * ChartTimeRangeRow — preset time-range buttons above timeseries charts.
 *
 * Pattern lifted from Apple Stocks / Robinhood / Plotly rangeselector — a
 * row of `1M / 3M / 6M / YTD / 1Y / 2Y / ALL` buttons that the user taps
 * to reframe the chart's x-domain.
 *
 * Important: ranges are computed relative to the DATA'S most recent
 * point, not `Date.now()`. Environmental data often has months-old
 * latest samples; "1M" should mean "the last month of *sampled* data,"
 * not "the calendar past 30 days" which would show nothing.
 *
 * Active range is highlighted; "ALL" is the rest-state default.
 */

import { useMemo } from 'react';

export type RangePresetKey = '1M' | '3M' | '6M' | 'YTD' | '1Y' | '2Y' | 'ALL';

interface Props {
  /** Earliest data timestamp (ms). */
  dataMinMs: number;
  /** Latest data timestamp (ms) — used as the "now" anchor for relative ranges. */
  dataMaxMs: number;
  /** Currently-applied x-domain (ms). */
  activeDomain: readonly [number, number];
  /** Fired when the user picks a preset; receives the new domain in ms. */
  onSelect: (domain: [number, number]) => void;
  /** Optional reset button (visible when the user has zoomed away from base). */
  isZoomed?: boolean;
  onReset?: () => void;
}

const PRESET_KEYS: RangePresetKey[] = ['1M', '3M', '6M', 'YTD', '1Y', '2Y', 'ALL'];

const MS_PER_DAY = 86_400_000;

function computeRange(
  key: RangePresetKey,
  dataMinMs: number,
  dataMaxMs: number,
): [number, number] | null {
  if (key === 'ALL') return [dataMinMs, dataMaxMs];
  if (key === 'YTD') {
    const end = new Date(dataMaxMs);
    const yearStart = Date.UTC(end.getUTCFullYear(), 0, 1);
    return [Math.max(yearStart, dataMinMs), dataMaxMs];
  }
  const days =
    key === '1M' ? 31 :
    key === '3M' ? 92 :
    key === '6M' ? 184 :
    key === '1Y' ? 366 :
    key === '2Y' ? 731 :
    null;
  if (days === null) return null;
  return [Math.max(dataMaxMs - days * MS_PER_DAY, dataMinMs), dataMaxMs];
}

/** Is the currently-applied domain within ~1 day of the preset's range? */
function domainMatches(
  candidate: [number, number] | null,
  active: readonly [number, number],
): boolean {
  if (!candidate) return false;
  const tol = MS_PER_DAY;
  return (
    Math.abs(candidate[0] - active[0]) <= tol &&
    Math.abs(candidate[1] - active[1]) <= tol
  );
}

export function ChartTimeRangeRow({
  dataMinMs,
  dataMaxMs,
  activeDomain,
  onSelect,
  isZoomed,
  onReset,
}: Props) {
  const presets = useMemo(
    () =>
      PRESET_KEYS.map((key) => ({
        key,
        range: computeRange(key, dataMinMs, dataMaxMs),
      })),
    [dataMinMs, dataMaxMs],
  );

  return (
    <div className="flex items-center gap-1 mb-2 text-[11px]" style={{ color: 'var(--dv-text-muted)' }}>
      {presets.map(({ key, range }) => {
        const isActive = domainMatches(range, activeDomain);
        const disabled = !range || range[1] - range[0] <= 0;
        return (
          <button
            key={key}
            type="button"
            disabled={disabled}
            onClick={() => range && onSelect(range)}
            className="px-2 py-0.5 rounded transition-colors"
            style={{
              background: isActive ? 'var(--dv-pill-bg)' : 'transparent',
              color: isActive
                ? 'var(--dv-text-primary)'
                : disabled
                  ? 'var(--dv-text-disabled)'
                  : 'var(--dv-text-muted)',
              border: `1px solid ${isActive ? 'var(--dv-border-subtle)' : 'transparent'}`,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontVariantNumeric: 'tabular-nums',
            }}
            onMouseEnter={(e) => {
              if (!isActive && !disabled) {
                e.currentTarget.style.color = 'var(--dv-text-primary)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isActive && !disabled) {
                e.currentTarget.style.color = 'var(--dv-text-muted)';
              }
            }}
          >
            {key}
          </button>
        );
      })}
      {isZoomed && onReset && (
        <button
          type="button"
          onClick={onReset}
          className="ml-2 px-2 py-0.5 rounded text-[10px] tracking-wider transition-colors"
          style={{
            color: 'var(--dv-text-muted)',
            border: '1px solid var(--dv-border-subtle)',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--dv-text-primary)';
            e.currentTarget.style.background = 'var(--dv-bg-card-hover)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--dv-text-muted)';
            e.currentTarget.style.background = 'transparent';
          }}
          title="Reset zoom"
        >
          RESET
        </button>
      )}
    </div>
  );
}
